<template>
    <section
        class="stf-radio-choice"
        :class="[
            { 'stf-radio-choice--selected': modelValue },
            { 'stf-radio-choice--inverted': inverted },
            `stf-radio-choice--${size}`,
            `stf-radio-choice--${color}`
        ]"
        @click="$emit('update:model-value', true)"
    >
        <div class="stf-radio-choice-section stf-radio-choice-section__radio-button">
            <StfRadioButton
                :model-value="modelValue ?? false"
                :value="true"
                :name="title"
                :size="'medium'"
                :color="color"
            />
           
        </div>
        <div class="stf-radio-choice-section stf-radio-choice-section__title">
            <div class="stf-radio-choice-title">{{ title }}</div>
            <div v-if="subtitle" class="stf-radio-choice-subtitle" v-html="subtitle"></div>
        </div>
        <div class="stf-radio-choice-section stf-radio-choice-section__items">
            <div
                v-for="(item, index) in items"
                :key="index"
                class="stf-radio-choice-item"
            >
                <img
                    v-if="item.type === 'image'"
                    :src="item.src"
                    :alt="item.text"
                    class="stf-radio-choice-item-content"
                />
                <div v-else class="stf-radio-choice-item-content">{{ item.text }}</div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import StfRadioButton from '@/ui/radio-button/StfRadioButton.vue';
import type { RadioItem } from './type';

interface Props {
    title: string;
    subtitle?: string;
    items: RadioItem[];
    modelValue?: boolean;
    inverted?: boolean;
    size?: 'medium' | 'large';
    color?: 'primary' | 'default';
}

defineEmits<{
    'update:model-value': [value: boolean]
}>();

withDefaults(defineProps<Props>(), {
    title: 'Image / Logo',
    items: () => [],
    size: 'medium',
    inverted: false,
    color: 'default'
});
</script>

<style lang="scss" scoped>
@use "bulma/sass/utilities/mixins";
@use '@/assets/sass/_variables' as vars;

.stf-radio-choice {
  display: flex;
  align-items: center;
  padding: 0 8px;
  gap: 8px;
  background-color: vars.$color-gray-50;
  border-radius: 10px;
  border: 1px solid vars.$color-gray-300;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: vars.$color-gray-100;
  }

  &--medium {
    height: 60px;
    border-radius: 5px;

    @include mixins.mobile {
      height: 40px;
    }
  }

  &--large {
    height: 100px;
    padding: 0 10px;
    gap: 12px;

    @include mixins.mobile {
      height: 80px;
    }
  }

  @include mixins.mobile {
    padding: 0 8px;
    justify-content: space-between;
  }
}

.stf-radio-choice--primary.stf-radio-choice--selected {
  background-color: vars.$color-success-light;
  border-color: vars.$color-success;
}

.stf-radio-choice-section {
  display: flex;
  align-items: center;
  gap: 15px;

  .stf-radio-choice--medium & {
    gap: 10px;
  }

  @include mixins.mobile {
    gap: 10px;
  }
}

.stf-radio-choice-title {
  font-family: vars.$font-family-primary;
  font-weight: vars.$font-weight-medium;
  font-size: vars.$font-size-25;
  color: vars.$color-gray-600;

  .stf-radio-choice--medium & {
    font-size: vars.$font-size-17;
    font-weight: vars.$font-weight-semibold;

    @include mixins.mobile {
      font-size: vars.$font-size-14;
    }
  }

  @include mixins.mobile {
    font-weight: vars.$font-weight-semibold;
    font-size: vars.$font-size-17;
  }
}

.stf-radio-choice-section__items {
  width: 23%;
  justify-content: center;
}

.stf-radio-choice-section__title {
    flex: 1;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
  }

.stf-radio-choice--inverted {
  flex-direction: row-reverse;
}

.stf-radio-choice-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

.stf-radio-choice-item-content {
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-16;
  color: vars.$color-gray-600;
  max-width: 110px;
  max-height: 41px;
  object-fit: contain;

  .stf-radio-choice--medium & {
    font-size: vars.$font-size-14;
    max-width: 90px;
    max-height: 35px;
  }
}
</style>
